import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, Pets } from '@mui/icons-material';
import { Plus, Wallet as WalletIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import TopUpDialog from './TopUpDialog';
import { isHandlerSite } from '../config/siteMode';
import {
  INK, PAPER, COBALT, MINT, VIOLET, FONT_DISPLAY, FONT_MONO,
} from '../theme/kiff';

/**
 * Role pill in the logo lockup — one of the three redundant role signals
 * (ground colour, top-bar underline, pill). The owner surface gets no pill:
 * it IS the product. Mint and violet are role-reserved colours.
 */
const RolePill = ({ label, bg, fg }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      fontSize: 10,
      letterSpacing: '0.06em',
      backgroundColor: bg,
      color: fg,
      border: `1.5px solid ${INK}`,
      borderRadius: 99,
      px: 1,
      py: 0.25,
      ml: 1,
      lineHeight: 1.4,
    }}
  >
    {label}
  </Box>
);

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { balance, loading: walletLoading } = useWallet();

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const handleUserMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);
  const handleMobileMenuOpen = (event) => setMobileMenuAnchor(event.currentTarget);
  const handleMobileMenuClose = () => setMobileMenuAnchor(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    handleUserMenuClose();
  };

  const isStaff = user?.is_staff;
  const onAdminSurface = pathname.startsWith('/admin');

  // Top-bar underline is a role signal: ink for owner, mint for handler,
  // violet for admin. 2.5px everywhere — it doubles as the bar's keyline.
  const underline = onAdminSurface ? VIOLET : isHandlerSite ? MINT : INK;

  // The handler site wears the dispatcher chrome: ink bar, paper lockup,
  // mint paw. Owner and admin keep the light bar.
  const barBg = isHandlerSite ? INK : '#FFFFFF';
  const barFg = isHandlerSite ? PAPER : INK;

  // Menu depends on which site this build is (owner vs handler) and staff.
  const userMenuItems = !isAuthenticated
    ? []
    : isStaff
    ? [
        // Staff are a separate world: admin portal only. (Owner site only.)
        { label: 'Admin Portal', action: () => navigate('/admin/dashboard') },
        { label: 'Logout', action: handleLogout },
      ]
    : isHandlerSite
    ? [
        { label: 'Dashboard', action: () => navigate('/dashboard') },
        { label: 'Profile', action: () => navigate('/profile') },
        { label: 'Payments', action: () => navigate('/payments') },
        { label: 'Logout', action: handleLogout },
      ]
    : [
        // Owner site: owner-only features.
        { label: 'Dashboard', action: () => navigate('/owner/dashboard') },
        { label: 'My Pets', action: () => navigate('/pets') },
        { label: 'Book a Service', action: () => navigate('/book-service') },
        { label: 'Profile', action: () => navigate('/profile') },
        { label: 'Wallet', action: () => navigate('/wallet') },
        { label: 'Payments', action: () => navigate('/payments') },
        { label: 'Logout', action: handleLogout },
      ];

  const mobileMenuItems = [
    ...(isAuthenticated ? userMenuItems : [
      { label: 'Login / Sign Up', action: () => navigate('/auth') },
    ]),
  ];

  // Credit balance, always on show: white pill, keyline, mono amount.
  const walletChip = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        border: `2px solid ${INK}`,
        borderRadius: 99,
        pl: 1.5,
        pr: 0.5,
        py: 0.25,
      }}
    >
      <Box
        onClick={() => navigate('/wallet')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          color: 'text.primary',
        }}
      >
        <WalletIcon size={16} />
        <Typography
          variant="body2"
          sx={{ fontFamily: FONT_MONO, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
        >
          {walletLoading ? '—' : `R${balance.toFixed(2)}`}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label="Add credits"
        title="Add credits"
        onClick={() => setTopUpOpen(true)}
        sx={{ ml: 0.5, color: theme.palette.primary.main }}
      >
        <Plus size={16} />
      </IconButton>
    </Box>
  );

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: barBg,
        borderBottom: `2.5px solid ${underline}`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo lockup: paw + PETTREK in Archivo 900 + role pill. */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <Pets sx={{ color: isHandlerSite ? MINT : COBALT, mr: 0.75, fontSize: '1.75rem' }} />
          <Typography
            component="div"
            sx={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 900,
              fontSize: 17,
              letterSpacing: '-0.01em',
              textTransform: 'uppercase',
              color: barFg,
            }}
          >
            PetTrek
          </Typography>
          {isHandlerSite && <RolePill label="HANDLER" bg={MINT} fg={INK} />}
          {!isHandlerSite && onAdminSurface && <RolePill label="ADMIN" bg={VIOLET} fg="#FFF" />}
        </Box>

        {/* Admin identity reads as data, per the admin voice. */}
        {onAdminSurface && isStaff && !isMobile && (
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: 12, color: 'text.secondary' }}>
            {user?.email}
          </Typography>
        )}

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAuthenticated ? (
              <>
                {isStaff ? (
                  <Button
                    color="inherit"
                    sx={{ color: 'text.primary' }}
                    onClick={() => navigate('/admin/dashboard')}
                  >
                    Admin Portal
                  </Button>
                ) : isHandlerSite ? (
                  <Button
                    color="inherit"
                    sx={{ color: barFg }}
                    onClick={() => navigate('/dashboard')}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      color="inherit"
                      sx={{ color: 'text.primary' }}
                      onClick={() => navigate('/owner/dashboard')}
                    >
                      Dashboard
                    </Button>
                    <Button
                      color="inherit"
                      sx={{ color: 'text.primary' }}
                      onClick={() => navigate('/pets')}
                    >
                      My Pets
                    </Button>
                    <Button
                      color="inherit"
                      sx={{ color: 'text.primary' }}
                      onClick={() => navigate('/book-service')}
                    >
                      Book Service
                    </Button>
                    {walletChip}
                  </>
                )}
                <IconButton onClick={handleUserMenuOpen}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: isHandlerSite ? MINT : COBALT,
                      color: isHandlerSite ? INK : '#FFF',
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {userMenuItems.map((item, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        item.action();
                        handleUserMenuClose();
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <Button variant="contained" onClick={() => navigate('/auth')}>
                Login / Sign Up
              </Button>
            )}
          </Box>
        )}

        {/* Mobile Navigation */}
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAuthenticated && !isStaff && !isHandlerSite && walletChip}
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuOpen}
              sx={{ color: barFg }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {mobileMenuItems.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => {
                    item.action();
                    handleMobileMenuClose();
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Toolbar>

      <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </AppBar>
  );
};

export default Navbar;
